import React, { useState } from 'react';
import { BusinessPlan } from '../../types/businessPlan';

interface SavedPlansNavigationProps {
  savedPlans: BusinessPlan[];
  currentPlanId: string | null;
  onSelectPlan: (plan: BusinessPlan) => void;
  onCreateNewPlan: () => void;
  onDeletePlan: (planId: string) => void;
  onDeleteAllPlans: () => void;
  onForceCleanup?: () => void;
  maxSavedPlans?: number;
}

const SavedPlansNavigation: React.FC<SavedPlansNavigationProps> = ({
  savedPlans,
  currentPlanId,
  onSelectPlan,
  onCreateNewPlan,
  onDeletePlan,
  onDeleteAllPlans,
  onForceCleanup,
  maxSavedPlans = 5
}) => {
  const [expandedPlans, setExpandedPlans] = useState<Record<string, boolean>>({});

  // Format date for display
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  // Handle delete with confirmation
  const handleDelete = (e: React.MouseEvent, planId: string) => {
    e.stopPropagation(); // Prevent triggering the parent onClick
    if (window.confirm('Are you sure you want to delete this business plan? This action cannot be undone.')) {
      onDeletePlan(planId);
    }
  };

  // Handle bulk delete with confirmation
  const handleDeleteAll = (e: React.MouseEvent) => {
    e.preventDefault();
    if (window.confirm(`Are you sure you want to delete ALL ${savedPlans.length} business plans? This action cannot be undone.`)) {
      onDeleteAllPlans();
    }
  };

  // Toggle plan expansion
  const togglePlanExpansion = (e: React.MouseEvent, planId: string) => {
    e.stopPropagation(); // Prevent triggering the plan selection
    setExpandedPlans(prev => ({
      ...prev,
      [planId]: !prev[planId]
    }));
  };

  return (
    <div className="saved-plans-navigation p-4 mb-6 w-full">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4 gap-3">
        <h2 className="text-xl font-semibold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">Saved Business Plans</h2>
        <div className="flex flex-wrap gap-2">
          {savedPlans.length > 0 && (
            <button
              onClick={handleDeleteAll}
              className="px-4 py-2 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-lg shadow-sm hover:shadow-md transition-all duration-300 transform hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-red-400 flex items-center"
              title="Delete all business plans"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              Delete All
            </button>
          )}
          {onForceCleanup && (
            <button
              onClick={onForceCleanup}
              className="px-4 py-2 bg-gradient-to-r from-red-700 to-red-800 text-white rounded-lg shadow-sm hover:shadow-md transition-all duration-300 transform hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-red-500 flex items-center"
              title="Force delete all business plans from database"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
              Force Cleanup
            </button>
          )}
          <button
            onClick={onCreateNewPlan}
            disabled={savedPlans.length >= maxSavedPlans}
            className="px-5 py-2.5 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg shadow-lg hover:shadow-blue-500/50 transition-all duration-300 transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none disabled:shadow-none flex items-center"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            Create Plan From Your Idea
          </button>
        </div>
      </div>

      {savedPlans.length === 0 ? (
        <div className="text-center py-6 bg-white/40 backdrop-blur-sm p-6 rounded-lg shadow-sm border border-white/30">
          <p className="text-gray-600">No saved business plans. Create your first plan!</p>
        </div>
      ) : (
        <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1 w-full">
          {savedPlans.map((plan) => (
            <div 
              key={plan.id}
              className={`relative overflow-hidden rounded-lg cursor-pointer transition-all duration-300 ${
                currentPlanId === plan.id 
                ? 'bg-blue-50/70 backdrop-blur-sm border border-blue-300/50 shadow-md'
                : 'bg-white/40 backdrop-blur-sm hover:bg-white/60 border border-white/40 shadow-sm hover:shadow'
              }`}
            >
              <div className={`absolute inset-0 bg-gradient-to-r ${
                currentPlanId === plan.id
                ? 'from-blue-400/10 to-indigo-400/10'
                : 'from-gray-200/5 to-gray-100/5'
              }`}></div>
              <div 
                className="relative z-10 p-3 flex justify-between items-center"
                onClick={() => onSelectPlan(plan)}
              >
                <div className={`font-medium ${
                  currentPlanId === plan.id
                  ? 'text-blue-700'
                  : 'text-gray-700'
                } break-words pr-8 flex-1`}>
                  {plan.title || `Business Plan ${plan.id?.substring(0, 4)}`}
                  {currentPlanId === plan.id && (
                    <span className="ml-2 text-xs font-medium text-blue-600 bg-blue-100/50 px-2 py-0.5 rounded-full">
                      Current
                    </span>
                  )}
                </div>
                
                <div className="flex items-center space-x-1 flex-shrink-0">
                  {/* Delete button */}
                  <button
                    onClick={(e) => handleDelete(e, plan.id!)}
                    className="text-gray-400 hover:text-red-500 p-1.5 rounded-full hover:bg-red-50/70 transition-all duration-300 backdrop-blur-sm"
                    title="Delete business plan"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                  
                  {/* Expand/collapse button */}
                  <button 
                    className="text-gray-500 hover:text-blue-600 p-1.5 rounded-full hover:bg-blue-50/70 transition-all duration-300 backdrop-blur-sm"
                    onClick={(e) => togglePlanExpansion(e, plan.id!)}
                    title={expandedPlans[plan.id!] ? "Collapse details" : "Expand details"}
                  >
                    <svg 
                      xmlns="http://www.w3.org/2000/svg" 
                      className={`h-4 w-4 transition-transform duration-300 ${expandedPlans[plan.id!] ? 'transform rotate-180' : ''}`} 
                      fill="none" 
                      viewBox="0 0 24 24" 
                      stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                </div>
              </div>
              
              {/* Expandable content */}
              {expandedPlans[plan.id!] && (
                <div className="relative z-10 px-3 pb-3 pt-1 border-t border-white/30">
                  <div className="mt-2 flex flex-wrap gap-2">
                    <div className="text-sm text-gray-500 bg-white/50 px-2 py-1 rounded-md">
                      Updated: {plan.updated_at ? formatDate(plan.updated_at) : 'N/A'}
                    </div>
                    {plan.sections && (
                      <div className="text-sm text-gray-500 bg-white/50 px-2 py-1 rounded-md">
                        <span className="font-medium">Sections:</span> {Object.keys(plan.sections).length}
                      </div>
                    )}
                  </div>
                  <div className="text-sm text-gray-600 mt-2 bg-white/60 p-2 rounded-md backdrop-blur-sm">
                    {plan.businessIdea?.title || 'No associated idea'}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      
      {savedPlans.length >= maxSavedPlans && (
        <div className="mt-4 text-sm text-amber-600 bg-amber-50/80 backdrop-blur-sm p-3 rounded-lg border border-amber-200/50 shadow-sm relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-amber-100/20 to-amber-200/20"></div>
          <div className="relative z-10 flex items-start">
            <svg className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <p>You've reached the maximum of {maxSavedPlans} saved plans. Delete older plans to create new ones.</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default SavedPlansNavigation;
